import { io } from 'socket.io-client';
import { API } from '../config/apiConfig';

class SocketService {
  constructor() {
    this.socket = null;
    this.onCallOffer = null;
    this.onCallEnd = null;
    this.connectionListeners = [];
    this.iceCandidateHandler = null;
    this.pendingIceCandidates = [];
  }

  connect(token) {
    if (this.socket?.connected) return;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = io(API.socketUrl, {
      auth: { token },
      transports: ['polling', 'websocket'],
      forceNew: true,
    });

    this.socket.on('connect', () => {
      this.connectionListeners.forEach((fn) => fn(true));
    });

    this.socket.on('disconnect', () => {
      this.connectionListeners.forEach((fn) => fn(false));
    });

    this.socket.on('connect_error', () => {
      this.connectionListeners.forEach((fn) => fn(false));
    });

    this.socket.on('call:offer', (data) => {
      if (this.onCallOffer) this.onCallOffer(data);
    });

    this.socket.on('call:end', (data) => {
      if (this.onCallEnd) this.onCallEnd(data);
    });

    // Buffer incoming ICE candidates until the CallScreen registers a handler,
    // otherwise candidates sent before the callee clicks "Answer" are lost.
    this.socket.on('call:ice-candidate', (data) => {
      if (this.iceCandidateHandler) {
        this.iceCandidateHandler(data);
      } else {
        this.pendingIceCandidates.push(data);
      }
    });
  }

  setIceCandidateHandler(handler) {
    this.iceCandidateHandler = handler;
    const buffered = this.pendingIceCandidates.splice(0);
    buffered.forEach((data) => {
      if (this.iceCandidateHandler) this.iceCandidateHandler(data);
    });
  }

  clearIceCandidateHandler() {
    this.iceCandidateHandler = null;
  }

  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.onCallOffer = null;
    this.onCallEnd = null;
    this.connectionListeners = [];
    this.iceCandidateHandler = null;
    this.pendingIceCandidates = [];
  }

  onConnectionChange(fn) {
    this.connectionListeners.push(fn);
    return () => {
      this.connectionListeners = this.connectionListeners.filter((l) => l !== fn);
    };
  }

  on(event, handler) {
    if (this.socket) {
      this.socket.off(event);
      this.socket.on(event, handler);
    }
  }

  off(event) {
    if (this.socket) {
      this.socket.off(event);
    }
  }

  emit(event, data) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  sendMessage(conversationId, content, type = 'text', mediaUrl = null) {
    this.emit('chat:send', { conversationId, type, content, mediaUrl });
  }

  markRead(conversationId) {
    this.emit('chat:read', { conversationId });
  }

  sendTyping(conversationId, isTyping) {
    this.emit('chat:typing', { conversationId, isTyping });
  }

  sendCallOffer(targetUserId, sdp, isVideo = false) {
    this.emit('call:offer', { targetUserId, sdp, video: isVideo });
  }

  sendCallAnswer(targetUserId, sdp) {
    this.emit('call:answer', { targetUserId, sdp });
  }

  sendIceCandidate(targetUserId, candidate) {
    this.emit('call:ice-candidate', { targetUserId, candidate });
  }

  endCall(targetUserId) {
    this.emit('call:end', { targetUserId });
  }

  missedCall(targetUserId) {
    this.emit('call:missed', { targetUserId });
  }

  get connected() {
    return this.socket?.connected || false;
  }
}

const socketService = new SocketService();
export default socketService;
